import AdyenCheckout from '@adyen/adyen-web'
import UIElement from '@adyen/adyen-web/dist/types/components/UIElement'
import AdyenCheckoutError from '@adyen/adyen-web/dist/types/core/Errors/AdyenCheckoutError'
import { useCallback, useState } from 'react'
import { AdyenResultStatus } from '../types/adyenResultStatus.enum'

const ADYEN_CLIENT_KEY = process.env.VITE_BOOKING_ADYEN_CLIENT_KEY
const ADYEN_ENVIRONMENT = process.env.VITE_ADYEN_ENVIRONMENT

const useAdyenSession = (
    session: { [key: string]: string },
    onCompleted: (code: AdyenResultStatus) => void,
    onError: (error: AdyenCheckoutError) => void,
    dropInProperties = {},
    translations = {}
) => {
    const [error, setError] = useState<unknown | undefined>()

    const createDropin = useCallback(async () => {
        if (ADYEN_CLIENT_KEY && ADYEN_ENVIRONMENT) {
            try {
                const configuration = {
                    environment: ADYEN_ENVIRONMENT, // Change to 'live' for the live environment.
                    clientKey: ADYEN_CLIENT_KEY, // Public key used for client-side authentication: https://docs.adyen.com/development-resources/client-side-authentication
                    session,
                    onPaymentCompleted: (result: { resultCode: string; sessionData: string }) => {
                        onCompleted(result.resultCode as AdyenResultStatus)
                    },
                    onError: (error: AdyenCheckoutError, component: UIElement) => {
                        if (error.name !== 'CANCEL') {
                            onError(error)
                            console.error(error.name, error.message, error.stack, component)
                        }
                    },
                    // Any payment method specific configuration. Find the configuration specific to each payment method:  https://docs.adyen.com/payment-methods
                    // For example, this is 3D Secure configuration for cards:
                    paymentMethodsConfiguration: {
                        storedCard: {
                            hideCVC: true
                        },
                        card: {
                            hasHolderName: true,
                            holderNameRequired: true
                        }
                    },
                    message: 'anc',
                    linkText: 'terms',
                    link: 'https://www.adyen.com/terms-and-conditions',
                    translations
                }
                const checkout = await AdyenCheckout(configuration)
                const dropin = checkout.create('dropin', { ...dropInProperties })
                return dropin
            } catch (error) {
                setError(error)
            }
        } else {
            setError(new Error('Missing configuration variables'))
        }
    }, [session, onCompleted, onError])

    return [createDropin, error] as const
}

export default useAdyenSession
